import React, { useEffect } from 'react';
import type { FC } from 'react';

import type { LoadableType } from '@confluence/loadable';
import { LoadableAfterPaint } from '@confluence/loadable';
import {
	manuallyLogExpExposure,
	UNSAFE_expValEqualsNoExposure,
} from '@confluence/feature-experiments';
import { SuspenseWithDataFetcher } from '@confluence/query-preloader-tools/entry-points/SuspenseWithDataFetcher.ts';

import { EndOfPageRecPlaceholderContainer } from './SharedComponents.tsx';
import type { EndOfPageRecommendationProps } from './EndOfPageRecommendation.tsx';

// Cohort split for `confluence_eop_recs_twg_source_exp`, evaluated at loader
// activation to pick which arm's chunk loads (treatment -> TWG source; control
// (default) -> cc-smarts). Read WITHOUT firing exposure: exposure is logged once
// at page load from `EndOfPageRecommendationFinalLoader` below, so it isn't biased
// toward users who scroll far enough to activate this loader.
const loadRecommendationComponent = async () => {
	if (UNSAFE_expValEqualsNoExposure('confluence_eop_recs_twg_source_exp', 'isEnabled', true)) {
		return (
			await import(
				/* webpackChunkName: "loadable-EndOfPageRecommendationTwg" */ './EndOfPageRecommendationTwg.tsx'
			)
		).EndOfPageRecommendationTwg;
	}
	return (
		await import(
			/* webpackChunkName: "loadable-EndOfPageRecommendation" */ './EndOfPageRecommendation.tsx'
		)
	).EndOfPageRecommendation;
};

export const EndOfPageRecommendationLoader: LoadableType<EndOfPageRecommendationProps> =
	LoadableAfterPaint({
		loader: loadRecommendationComponent,
	});

export const EndOfPageRecommendationFinalLoader: FC<EndOfPageRecommendationProps> = (props) => {
	// Single, unbiased exposure point for `confluence_eop_recs_twg_source_exp`.
	// This runs client-side on mount — the earliest path shared by both arms and
	// before the scroll/loadable boundary — so exposure covers the whole page/blog
	// population, not just users who scroll to the recs. The cohort split reads with
	// UNSAFE_expValEqualsNoExposure, and the component's "recs shown" event still
	// scopes click-through to users who actually saw recommendations.
	useEffect(() => {
		manuallyLogExpExposure('confluence_eop_recs_twg_source_exp');
	}, []);

	// Uses EndOfPageRecommendationLoader (LoadableAfterPaint) rather than a LoadableActivatable
	// SCROLL_INTO_VIEW variant: that trigger fires setIsInViewport() synchronously via a ref callback
	// during React's commit/hydration phase, causing React error #421 when nested inside a
	// SuspenseWithDataFetcher boundary while scheduleSyncHydration is active.
	return (
		<SuspenseWithDataFetcher
			dataFetcherName="end-of-page-recommendations"
			fallback={<EndOfPageRecPlaceholderContainer />}
		>
			<EndOfPageRecommendationLoader {...props} />
		</SuspenseWithDataFetcher>
	);
};
